import { Box, Typography } from "@mui/material";
import { FC, useState } from "react";
import UnlockPremiumSvg from "@/assets/svg/UnlockPremiumSvg";
import { RequireAuth } from "@/components/RequireAuth";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import theme from "@/theme";
import ToggleView from "./ToggleView";
import TabNavigation from "./TabNavigation";
import Filters from "./Filters";
import SortTabs from "./SortTabs";
import { TRENDING_ITEMS_LIMIT } from "../RightSidebar";
import TrendingTable from "./TrendingTable";
import { TimeRangeValue } from "@/types/timeRanges";
import { styles } from "../stylesRightSidebar";
import PremiumSubscriptionModal from "@/components/UI/modal/PremiumSubscriptionModal/PremiumSubscriptionModal";

type SortTab = "post" | "likes" | "liked" | "sentiment";
type SortVariant = "Posts" | "Likes" | "Liked" | "Sentiment";
type TabType = "STOCK" | "SECTOR" | "INDUSTRY" | "ETF" | "INDEX" | "ALL";

interface TrendingProps {
  hasPremium: boolean;
  subscriptionLoading: boolean;
  tabs: { label: string; value: TabType }[];
  filterTime: TimeRangeValue;
  setFilterTime: (value: TimeRangeValue) => void;
  subtype: string[];
  setSubtype: (value: string[]) => void;
  activeSortTab: SortTab;
  setActiveSortTab: (value: SortTab) => void;
  sortOrder: "asc" | "desc";
  handleSort: () => void;
  hideAsc: boolean;
  hideDesc: boolean;
  sortVariantMap: Record<SortTab, SortVariant>;
  isFetching: boolean;
  sortedData: {
    name: string;
    posts: number;
    likes: number;
    sentiment?: number | null;
    symbol: string;
    industry?: string;
    sector?: string;
  }[];
  view: "all" | "follows";
  activeTab: TabType;
  toast: { message: string; success?: boolean } | null;
  setToast: React.Dispatch<React.SetStateAction<{ message: string; success?: boolean } | null>>;
}

const Trending: FC<TrendingProps> = ({
  hasPremium,
  subscriptionLoading,
  tabs,
  filterTime,
  setFilterTime,
  subtype,
  setSubtype,
  activeSortTab,
  setActiveSortTab,
  sortOrder,
  handleSort,
  isFetching,
  sortedData,
  view,
  activeTab,
  setToast,
}) => {
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const openPremiumModal = () => setPremiumModalOpen(true);
  const closePremiumModal = () => setPremiumModalOpen(false);
  const [showAll, setShowAll] = useState(false);

  const handleShowMoreClick = () => {
    setShowAll(!showAll);
  };
  const displayedData = showAll ? sortedData : sortedData.slice(0, 3);

  return (
    <Box sx={styles.trendingWrapper}>
      <PremiumSubscriptionModal
        open={premiumModalOpen}
        onClose={closePremiumModal}
        setToast={setToast}
      />

      {!hasPremium && !subscriptionLoading && showAll && (
        <Box position={"absolute"} sx={styles.tableBodyBox}>
          <UnlockPremiumSvg />
          <RequireAuth>
            <Typography
              onClick={openPremiumModal}
              color={theme.palette.custom.mainBlue}
              component="p"
              fontWeight={600}
              lineHeight="157%"
              variant="h6"
              sx={{ cursor: "pointer", textAlign: "center", zIndex: 999999 }}
            >
              Unlock Premium
            </Typography>
          </RequireAuth>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          maxHeight: "36px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={600}
          fontSize={18}
          lineHeight={24}
          fontFamily={"Inter"}
          color="text.primary"
        >
          Trending
        </Typography>

        <ToggleView
          premium={hasPremium}
          onPremiumClick={openPremiumModal}
          subscriptionLoading={subscriptionLoading}
        />
      </Box>
      <Box
        sx={{
          height: "24px",
          display: "flex",
          alignItems: "center",
          margin: "16px 0px 10px 0",
        }}
      >
        <TabNavigation tabs={tabs} />
      </Box>

      <Box>
        <Filters
          value={filterTime}
          onChange={setFilterTime}
          isPremium={hasPremium}
          subtype={subtype}
          onSubtypeChange={setSubtype}
        />

        <SortTabs
          activeTab={activeSortTab}
          setActiveTab={setActiveSortTab}
          order={sortOrder}
          view={view}
          onSortClick={(variant) => {
            handleSort();
            setActiveSortTab(
              variant === "Posts"
                ? "post"
                : variant === "Likes"
                  ? "likes"
                  : variant === "Liked"
                    ? "liked"
                    : "sentiment"
            );
          }}
        />

        {isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: showAll ? TRENDING_ITEMS_LIMIT * 73 : 3 * 73,
              position: "relative",
              filter: "none",
              zIndex: !hasPremium ? 1 : "auto",
            }}
          >
            <CustomLoader loading size={48} />
          </Box>
        ) : (
          <TrendingTable
            premium={hasPremium}
            sortedData={displayedData}
            sortOrder={sortOrder}
            handleSort={handleSort}
            activeSortTab={activeSortTab}
            filterTime={filterTime}
            view={view}
            activeTab={activeTab}
            subtype={subtype}
            subscriptionLoading={subscriptionLoading}
          />
        )}

        <Box
          sx={{
            width: "304px",
            height: "42px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            opacity: 1,
            transform: "rotate(0deg)",
            cursor: "pointer",
          }}
          onClick={handleShowMoreClick}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "15px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: theme.palette.custom.mainBlue,
            }}
          >
            {!showAll ? "Show more" : "Show less"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Trending;
